import React from 'react';
import { FolderGit2, Plus, Trash2, Link as LinkIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Project } from '../types';
import { DateInput } from '../components/DateInput';

interface ProjectsProps {
  projects: Project[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}

export function Projects({ projects, setProjects }: ProjectsProps) {
  const addProject = () => {
    setProjects([...projects, {
      id: projects.length + 1,
      title: '',
      description: '',
      technologies: '',
      link: '',
      startDate: '',
      endDate: ''
    }]);
  };

  const removeProject = (id: number) => {
    setProjects(projects.filter(proj => proj.id !== id));
  };

  const updateProject = (id: number, field: keyof Project, value: string) => {
    setProjects(projects.map(proj => 
      proj.id === id ? { ...proj, [field]: value } : proj
    ));
  };

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-semibold flex items-center gap-2">
        <FolderGit2 className="w-5 h-5" />
        Projects
      </h2>

      <AnimatePresence>
        {projects.map((project) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-4 p-4 border border-gray-200 rounded-lg hover:border-indigo-300 
              transition-colors duration-200"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Project Title</label>
                <input
                  type="text"
                  value={project.title}
                  onChange={(e) => updateProject(project.id, 'title', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <DateInput
                label="Start Date"
                value={project.startDate}
                onChange={(value) => updateProject(project.id, 'startDate', value)}
                required
              />

              <DateInput
                label="End Date"
                value={project.endDate}
                onChange={(value) => updateProject(project.id, 'endDate', value)}
              />

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={project.description}
                  onChange={(e) => updateProject(project.id, 'description', e.target.value)}
                  required
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Technologies Used</label>
                <input
                  type="text"
                  value={project.technologies}
                  onChange={(e) => updateProject(project.id, 'technologies', e.target.value)}
                  required
                  placeholder="e.g., React, Node.js, TypeScript"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                  <LinkIcon className="w-4 h-4" />
                  Project Link (Optional)
                </label>
                <input
                  type="url"
                  value={project.link}
                  onChange={(e) => updateProject(project.id, 'link', e.target.value)}
                  placeholder="https://"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>
            </div>

            {projects.length > 1 && (
              <motion.button
                type="button"
                onClick={() => removeProject(project.id)}
                className="text-red-600 hover:text-red-700 flex items-center gap-1"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 className="w-4 h-4" />
                Remove Project
              </motion.button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={addProject}
        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Plus className="w-4 h-4" />
        Add Another Project
      </motion.button>
    </motion.div>
  );
}